import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetTossExposureQuery } from '../services/sports/sportsApi';
import { useLazySearchDownlineByUserIdQuery } from '../services/auth/authApi';
import Loading from '../components/Loading';

const DownArrowIcon = () => (
  <svg viewBox="64 64 896 896" focusable="false" width="1em" height="1em" fill="currentColor">
    <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3.1-12.7-6.4-12.7z" />
  </svg>
);

const CustomSelect = ({ value, onChange, options, placeholder, searchable, onSearchChange, searchValue, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative w-full">
      {searchable ? (
        <input
          type="text"
          value={searchValue || ''}
          onChange={(e) => { onSearchChange(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder || 'Search...'}
          className="w-full border border-gray-300 px-3 py-2 bg-white"
          style={{ borderRadius: 0, minHeight: '38px' }}
        />
      ) : (
        <div
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center justify-between border border-gray-300 px-3 py-2 cursor-pointer bg-white"
          style={{ borderRadius: 0, minHeight: '38px' }}
        >
          <span className={options.find(o => o.value === value) ? 'text-black' : 'text-gray-400'}>
            {options.find(o => o.value === value)?.label || placeholder || 'Select'}
          </span>
          <span className="text-gray-400"><DownArrowIcon /></span>
        </div>
      )}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-300 z-20 shadow-lg max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-3 py-2 text-gray-500 text-sm">Loading...</div>
            ) : options.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">No options found</div>
            ) : (
              options.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setIsOpen(false); }}
                  className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${opt.value === value ? 'bg-blue-100' : ''}`}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
};

const TossBetsPage = () => {
  const navigate = useNavigate();
  const { matchId, matchName } = useParams();
  const [selectedClientId, setSelectedClientId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const searchTimeoutRef = useRef(null);

  const [searchDownline, { data: searchResponse, isLoading: clientsLoading }] = useLazySearchDownlineByUserIdQuery();
  const searchResults = searchResponse?.data || [];

  const handleSearchInput = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) return;
    searchTimeoutRef.current = setTimeout(() => {
      searchDownline({ userId: value.trim(), userType: 'Client' });
    }, 500);
  };

  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const { data: apiData, isLoading, error: apiError, refetch } = useGetTossExposureQuery({
    eventId: matchId,
    clientId: selectedClientId || undefined,
    page: currentPage,
    limit,
  }, { skip: !matchId });

  useEffect(() => {
    if (matchId) refetch();
  }, [selectedClientId, currentPage, matchId, refetch]);

  const bets = apiData?.bets || [];
  const runners = apiData?.runners || [];
  const myBook = apiData?.myBook || {};
  const allBook = apiData?.allBook || {};
  const pagination = apiData?.pagination || {};
  const error = apiError ? (apiError.data?.message || apiError.message || 'Failed to load toss bets') : null;

  const userOptions = searchResults.map((c) => ({
    value: c.userId,
    label: `${c.fullName || c.username || c.userId} (${c.userId})`,
  }));

  const handleClientChange = (val) => {
    setSelectedClientId(val);
    const selected = searchResults.find((c) => c.userId === val);
    if (selected) setSearchInput(selected.fullName || selected.username || selected.userId);
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  const decodedMatchName = matchName ? decodeURIComponent(matchName) : '';

  return (
    <div className="w-full bg-gray-100">
      <div className="w-full">
        <div className="bg-white">
          {/* Header */}
          <div className="bg-[#3c4f5c] px-5 py-3 flex items-center justify-between">
            <span className="text-xl text-white font-normal">Toss Book — {decodedMatchName}</span>
            <button onClick={() => navigate(-1)} className="bg-white text-gray-800 px-6 py-1.5 hover:bg-gray-100">
              Back
            </button>
          </div>

          {/* Runner Book Table */}
          {runners.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#3c4f5c] text-white">
                    <th className="p-3 border border-gray-600 text-left font-medium">Runner</th>
                    <th className="p-3 border border-gray-600 text-left font-medium">My Book</th>
                    <th className="p-3 border border-gray-600 text-left font-medium">Total Book</th>
                  </tr>
                </thead>
                <tbody>
                  {runners.map((runner, index) => {
                    const myVal = myBook[runner.name] ?? 0;
                    const allVal = allBook[runner.name] ?? 0;
                    return (
                      <tr key={runner.sid} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-3 border border-gray-300">{runner.name}</td>
                        <td className={`p-3 border border-gray-300 font-medium ${myVal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {myVal.toFixed(2)}
                        </td>
                        <td className={`p-3 border border-gray-300 font-medium ${allVal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {allVal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Filter Section */}
          <div className="mt-6">
            <div className="bg-[#3c4f5c] px-5 py-3">
              <span className="text-xl text-white font-medium">Filter Bets</span>
            </div>
            <div className="p-5 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Client<span className="text-red-500">*</span></label>
                  <CustomSelect
                    value={selectedClientId}
                    onChange={handleClientChange}
                    options={userOptions}
                    placeholder="Search User"
                    searchable
                    onSearchChange={handleSearchInput}
                    searchValue={searchInput}
                    loading={clientsLoading}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => { setSelectedClientId(''); setSearchInput(''); setCurrentPage(1); }}
                    className="bg-[#3c4f5c] text-white px-6 py-2 hover:bg-[#2d3e4a] text-sm"
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Toss Bets Table */}
          <div className="mt-6">
            <div className="bg-[#3c4f5c] px-5 py-3">
              <span className="text-xl text-white font-medium">Toss Bets</span>
            </div>

            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center py-12"><Loading /></div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-[#3c4f5c] text-white">
                    <tr>
                      <th className="border border-gray-600 p-3 text-left font-medium">Username</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Runner Name</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Bet Type</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Bet Price</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Bet Amount</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Status</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Winner</th>
                      <th className="border border-gray-600 p-3 text-left font-medium">Place Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {bets.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="p-8 text-center text-gray-400">No Data</td>
                      </tr>
                    ) : (
                      bets.map((bet, index) => (
                        <tr key={bet.betId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-300 p-3">
                            <div>{bet.fullName || bet.username || '-'}</div>
                            {bet.userId && <div className="text-xs text-gray-500">{bet.userId}</div>}
                          </td>
                          <td className="border border-gray-300 p-3">{bet.runner || '-'}</td>
                          <td className="border border-gray-300 p-3">
                            <span className="px-2 py-1 text-xs uppercase font-medium bg-blue-100 text-blue-700">
                              {bet.betType || 'Back'}
                            </span>
                          </td>
                          <td className="border border-gray-300 p-3">{bet.rate ?? '-'}</td>
                          <td className="border border-gray-300 p-3">{bet.amount ?? '-'}</td>
                          <td className="border border-gray-300 p-3">
                            <span className={`px-2 py-1 text-xs uppercase font-medium ${
                              bet.status === 'pending' ? 'bg-yellow-100 text-yellow-700'
                              : bet.status === 'won' ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                            }`}>
                              {bet.status || '-'}
                            </span>
                          </td>
                          <td className="border border-gray-300 p-3">{apiData?.winner?.name || '-'}</td>
                          <td className="border border-gray-300 p-3">{formatDate(bet.date || bet.createdAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="p-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages} ({pagination.totalDocs} total bets)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= pagination.totalPages}
                  className="px-4 py-2 bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TossBetsPage;
